import React from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import BlockIcon from "@mui/icons-material/Block";
import ReplayIcon from "@mui/icons-material/Replay";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import StopCircleIcon from "@mui/icons-material/StopCircle";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import StackedHeader from "../../../../helper-components/misc/StackedHeader";
import DataGridTable from "../../../../helper-components/table/DataGridTable";

import HighlightRequestEditForm from "./HighlightRequestEditForm";
import HighlightRequestAddStepper from "./HighlightRequestAddStepper";

import { columns, defaultTableOptions } from "../../../../helper-functions/table/highlightRequests";

import { createUploadObject } from "../../../../helper-functions/shared/miscUtils";
import {
    approveHighlightRequests,
    createHighlightRequest,
    deleteHighlightRequests,
    disapproveHighlightRequests,
    queryHighlightRequests,
    retryHighlightRequests,
    updateHighlightRequests,
    startProcessHighlightRequest,
    stopProcessHighlightRequest
} from "../../../../helper-functions/api/highlightQueries";
import { getLanguage } from "../../../../config/language/language";

/**
 * Table showing highlight requests.
 * If no data found, show error message instead.
 */
export default function HighlightRequestsTable(props) {
    const highlightRequests = props.backend.highlightRequests;

    const isProcessing = highlightRequests.data.some((request) => request.status === "IN_PROGRESS");

    const [disabled, setDisabled] = React.useState(false);

    const showCreateDialog = () => {
        props.showDialog({
            title: "Add new highlight request",
            component: <HighlightRequestAddStepper {...props} callback={(confirm, options) => handleCreate(confirm, options)} />
        });
    };

    const showDeleteDialog = () => {
        props.showDialog({
            title: "Delete highlight request(s)",
            message: `Are you sure you want to delete the ${highlightRequests.selected.length} selected highlight request(s)?`,
            actionTitle: "Delete",
            actionColor: "error",
            callback: (confirm) => handleDelete(confirm)
        });
    };

    const showUpdateDialog = () => {
        props.showDialog({
            title: "Edit highlight request",
            message: "Click 'Update' to confirm changes.",
            component: <HighlightRequestEditForm {...props} row={highlightRequests.selected?.[0]} callback={(confirm, options) => handleUpdate(confirm, options)} />
        });
    };

    const showApproveDialog = () => {
        props.showDialog({
            title: "Approve highlight request(s)",
            message: `Are you sure you want to approve the ${highlightRequests.selected.length} selected highlight request(s)?`,
            actionTitle: "Approve",
            actionColor: "success",
            callback: (confirm) => handleApprove(confirm)
        });
    };

    const showDisapproveDialog = () => {
        props.showDialog({
            title: "Disapprove highlight request(s)",
            message: `Are you sure you want to disapprove the ${highlightRequests.selected.length} selected requests?
            After disapproval, the requests will not be processed further.`,
            actionTitle: "Disapprove",
            actionColor: "error",
            callback: (confirm) => handleDisapprove(confirm)
        });
    };

    const showRetryDialog = () => {
        props.showDialog({
            title: "Retry highlight request(s)",
            message: `Are you sure you want to retry the ${highlightRequests.selected.length} selected highlight request(s)?`,
            actionTitle: "Retry",
            actionColor: "success",
            callback: (confirm) => handleRetry(confirm)
        });
    };

    const showStartProcessDialog = () => {
        props.showDialog({
            title: "Start processing highlight requests",
            message: `Are you sure you want to process the queued highlight requests?
            This will start the processing ahead of schedule and may take some time.`,
            actionTitle: "Begin processing",
            actionColor: "success",
            callback: (confirm) => handleStartProcess(confirm)
        });
    };

    const showStopProcessDialog = () => {
        props.showDialog({
            title: "Stop processing highlight requests",
            message: `Are you sure you want to stop the processing of in-progress highlight requests?
            This will finish the current highlight processing and force the active highlight requests into an error state, requiring approval again.`,
            actionTitle: "Stop processing",
            actionColor: "error",
            callback: (confirm) => handleStopProcess(confirm)
        });
    };

    const handleCreate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form", "_*"]);

        props.showSnackbar({ color: "info", message: "Creating highlight request...", duration: 5000 });
        createHighlightRequest(props, { body });
    };

    const handleDelete = (confirm) => {
        if (!confirm) return;

        const ids = highlightRequests.selected.map((request) => request.id);
        deleteHighlightRequests(props, { body: { ids }, ids });
    };

    const handleUpdate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form", "_*"]);

        updateHighlightRequests(props, { body, id: row.id }); // Using create endpoint for updates as well
    };

    const handleApprove = (confirm) => {
        if (!confirm) return;

        const ids = highlightRequests.selected.map((request) => request.id);
        approveHighlightRequests(props, { body: { ids } });
    };

    const handleDisapprove = (confirm) => {
        if (!confirm) return;

        const ids = highlightRequests.selected.map((request) => request.id);
        disapproveHighlightRequests(props, { body: { ids } });
    };

    const handleRetry = (confirm) => {
        if (!confirm) return;

        const ids = highlightRequests.selected.map((request) => request.id);
        retryHighlightRequests(props, { body: { ids } });
    };

    const handleStartProcess = (confirm) => {
        if (!confirm) return;
        setDisabled(true);

        startProcessHighlightRequest(props);

        setTimeout(() => setDisabled(false), 10000); // This query can take a bit of time in the backend
    };

    const handleStopProcess = (confirm) => {
        if (!confirm) return;
        setDisabled(true);

        stopProcessHighlightRequest(props);

        setTimeout(() => setDisabled(false), 10000); // This query can take a bit of time in the backend
    };

    return (
        <PaperFragment
            className="tour-highlight-requests-2 tour-highlight-requests-3"
            title={
                <StackedHeader
                    {...props}
                    title="Highlight Requests"
                    subtitle="View and manage highlight requests."
                    icons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: "Delete selected highlight requests",
                            disabled: highlightRequests.selected.length === 0 || highlightRequests.selected.some((r) => ["IN_PROGRESS"].includes(r.status)),
                            onClick: showDeleteDialog
                        },
                        {
                            icon: <EditIcon />,
                            tooltip: "Edit selected highlight request",
                            onClick: showUpdateDialog,
                            disabled: highlightRequests.selected.length !== 1
                        },
                        {
                            icon: <CheckIcon />,
                            tooltip: "Approve selected highlight requests",
                            onClick: showApproveDialog,
                            disabled: highlightRequests.selected.length === 0 || !highlightRequests.selected.every((r) => r.status === "AWAITING_APPROVAL")
                        },
                        {
                            icon: <BlockIcon />,
                            tooltip: "Disapprove selected highlight requests",
                            onClick: showDisapproveDialog,
                            disabled: highlightRequests.selected.length === 0 || highlightRequests.selected.some((r) => ["AWAITING_APPROVAL"].includes(r.status))
                        },
                        {
                            icon: <ReplayIcon />,
                            tooltip: "Retry selected highlight requests",
                            onClick: showRetryDialog,
                            disabled: highlightRequests.selected.length === 0 || !highlightRequests.selected.every((r) => r.status === "ERROR")
                        }
                    ]}
                    buttons={[
                        {
                            disabled: disabled,
                            icon: isProcessing ? <StopCircleIcon /> : <PlayCircleIcon />,
                            onClick: isProcessing ? showStopProcessDialog : showStartProcessDialog,
                            tooltip: isProcessing ? "Stop processing highlight requests" : "Start processing highlight requests",
                            size: "small",
                            className: "button-small"
                        },
                        {
                            icon: <AddIcon />,
                            title: "New highlight request",
                            onClick: showCreateDialog
                        }
                    ]}
                />
            }
            value={
                <DataGridTable
                    {...props}
                    {...defaultTableOptions}
                    rowHeight={60}
                    states={highlightRequests}
                    columns={columns(props)}
                    query={(options) => queryHighlightRequests(props, options)}
                    empty={getLanguage().NO_HIGHLIGHT_REQUESTS_FOUND}
                    refresh={10}
                />
            }
        />
    );
}
